import { ref, watch, computed } from 'vue';
import { useRoute } from 'vue-router';
import { isNotOnPublicPage } from '@/utils/http';
import { getActiveSubscription } from '@/data/session/session-globals';

const DEFAULT_UI_VERSION = 'current';
const NEW_UI_VERSION = 'new';
const PENDO_INTERNAL_SUB_ID = '5668600916475904';
const uiVersion = ref(DEFAULT_UI_VERSION);

function shouldApplyNewUi () {
    const isProd = window?.appEnvId === 'prod';
    if (!isProd) return true;

    return getActiveSubscription()?.id === PENDO_INTERNAL_SUB_ID;
}

function applyUiVersion (version) {
    if (version === NEW_UI_VERSION && shouldApplyNewUi()) {
        document.documentElement.setAttribute('data-ui', NEW_UI_VERSION);
    } else {
        document.documentElement.removeAttribute('data-ui');
    }
}

export default function useUiVersion () {
    const route = useRoute();

    const setUiVersion = (value) => {
        const updatedVersion = value ? NEW_UI_VERSION : DEFAULT_UI_VERSION;

        window.localStorage.setItem('pendo-ui-version', updatedVersion);
        uiVersion.value = updatedVersion;
        applyUiVersion(updatedVersion);
    };

    const initializeUiVersion = () => {
        const setupUiVersion = () => {
            uiVersion.value = window.localStorage.getItem('pendo-ui-version') || DEFAULT_UI_VERSION;
            applyUiVersion(uiVersion.value);
        };

        if (isNotOnPublicPage()) {
            setupUiVersion();
        } else {
            const unwatch = watch(() => route.path, (newPath) => {
                if (newPath && isNotOnPublicPage()) {
                    unwatch();
                    setupUiVersion();
                }
            });
        }
    };

    const isNewUi = computed(() => {
        return uiVersion.value === NEW_UI_VERSION;
    });

    return {
        isNewUi,
        setUiVersion,
        initializeUiVersion,
    };
}
